import { Eyebrow, RadioCard, RadioCardGroup, Stack } from '@leanin/ui';
import { useTranslations } from '@/i18n/translations';

// Two-card join-policy radio. Mirrors AdminAccessPolicyChoice's shape and
// API so Circle / Network / Space Visibility tabs can compose the two without
// drift. Copy adapts per kind via `entityLabel`.
//
// The wire still accepts `'open'` (legacy rows may carry it and the server
// route accepts it for callers that pre-date this UI), but it's no longer
// pickable from the admin frontend — every new save funnels through
// `'request'` or `'invite_only'`. If a leader visits a legacy row saved as
// `'open'`, none of the cards renders selected; selecting `'request'` or
// `'invite_only'` migrates the row on save.

export type AdminJoinPolicy = 'open' | 'request' | 'invite_only';

type Props = {
  readonly value: AdminJoinPolicy;
  readonly onChange: (next: AdminJoinPolicy) => void;
  /** "Circle" / "Network" / "Space" — wordings reference the entity by name. */
  readonly entityLabel: string;
};

export function AdminJoinPolicyChoice({ value, onChange, entityLabel }: Props) {
  const t = useTranslations('Group');
  const kind = entityLabel.toLowerCase();
  const options: ReadonlyArray<{ value: AdminJoinPolicy; label: string; lede: string }> = [
    {
      value: 'request',
      label: t('admin.join.requestLabel'),
      lede: t('admin.join.requestLede'),
    },
    {
      value: 'invite_only',
      label: t('admin.join.inviteOnlyLabel'),
      lede: t('admin.join.inviteOnlyLede'),
    },
  ];

  return (
    <Stack gap="md">
      <Eyebrow tone="accent">{t('admin.join.eyebrow')}</Eyebrow>
      <RadioCardGroup
        value={value}
        onValueChange={(v) => onChange(v as AdminJoinPolicy)}
        aria-label={t('admin.join.ariaLabel', { kind })}
      >
        {options.map((opt) => (
          <RadioCard
            key={opt.value}
            value={opt.value}
            title={opt.label}
            sub={opt.lede}
          />
        ))}
      </RadioCardGroup>
    </Stack>
  );
}
